import React, { useMemo, useState } from "react";
import axios from "axios";
import { FiSearch } from "react-icons/fi";
import { LoadingSpinner } from "../LoadingSpinner";
import {
	MediaOrphanReportDto,
	extractErrorMessage,
	formatBytes,
	formatDateTimeFr
} from "./checksCommon";

const CFG = { withCredentials: true, headers: { Accept: "application/json" } };

/** Combien de lignes d'orphelins le tableau affiche au maximum. */
const DISPLAY_CAP = 100;

/** Petit compteur « valeur + libellé » de la ligne d'agrégats. */
const Counter: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
	<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
		<div className="text-sm font-bold text-slate-800">{value}</div>
		<div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
	</div>
);

/**
 * « Médias orphelins » : audit STRICTEMENT en lecture seule de la médiathèque
 * (POST /api/storage/orphans/scan) : quels fichiers stockés ne sont référencés par aucun
 * document vivant, plus les lignes PdfExtractedTexts qui pointent vers un PDF disparu (la
 * fuite connue du delete). Aucun bouton de suppression ici, volontairement : un faux verdict
 * « orphelin » supprimé automatiquement serait irrécupérable : la suppression reste une action
 * humaine délibérée via la page Stockage.
 */
const MediaOrphansCard: React.FC = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [report, setReport] = useState<MediaOrphanReportDto | null>(null);

	const scan = async () => {
		if (loading) return;
		setLoading(true);
		setError(null);
		try {
			const res = await axios.post("/api/storage/orphans/scan", null, CFG);
			setReport((res.data ?? null) as MediaOrphanReportDto | null);
		} catch (err) {
			// 503 « scanner non configuré » et autres erreurs : le message FR du serveur gagne.
			setError(extractErrorMessage(err, "Échec de l'analyse des médias orphelins."));
		} finally {
			setLoading(false);
		}
	};

	const orphans = useMemo(() => {
		const files = Array.isArray(report?.files) ? report!.files : [];
		return files
			.filter((f) => f?.verdict === "orphan")
			.sort((a, b) => Number(b?.size ?? 0) - Number(a?.size ?? 0));
	}, [report]);

	const shown = orphans.slice(0, DISPLAY_CAP);
	const hidden = orphans.length - shown.length;
	const leaks = Array.isArray(report?.pdfExtractedTextLeaks) ? report!.pdfExtractedTextLeaks : [];

	return (
		<div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
			<div>
				<p className="text-base font-semibold text-slate-800">Médias orphelins</p>
				<p className="max-w-3xl text-sm text-slate-600">
					Recense les fichiers de la médiathèque qu'aucun document vivant (ni avatar) ne référence, ainsi que les
					textes extraits de PDF dont le fichier n'existe plus. Analyse en lecture seule : rien n'est supprimé ;
					la suppression reste une action manuelle via la page Stockage.
				</p>
			</div>

			<div className="flex flex-wrap items-center gap-3">
				<button
					type="button"
					onClick={scan}
					disabled={loading}
					className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{loading ? <LoadingSpinner size="sm" inline /> : <FiSearch className="h-4 w-4" />}
					{loading ? "Analyse en cours…" : "Analyser"}
				</button>
				{report?.scannedAt && (
					<p className="text-xs text-slate-500">
						Analysé le {formatDateTimeFr(report.scannedAt)}
						{typeof report.elapsedMs === "number" ? ` en ${(report.elapsedMs / 1000).toFixed(1).replace(".", ",")} s` : ""}.
					</p>
				)}
			</div>

			{error && (
				<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
					{error}
				</p>
			)}

			{report && (
				<>
					{/* Agrégats du scan. */}
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
						<Counter value={Number(report.totalFiles ?? 0)} label="Fichiers analysés" />
						<Counter value={Number(report.usedCount ?? 0)} label="Utilisés" />
						<Counter value={Number(report.orphanCount ?? 0)} label="Orphelins" />
						<Counter value={formatBytes(Number(report.orphanBytes ?? 0))} label="Poids des orphelins" />
					</div>

					{orphans.length === 0 ? (
						<p className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-sm font-medium text-emerald-800">
							Aucun média orphelin : chaque fichier stocké est référencé par au moins un document.
						</p>
					) : (
						<div className="space-y-2">
							<div className="overflow-x-auto rounded-xl border border-slate-200">
								<table className="min-w-full divide-y divide-slate-200 text-left">
									<thead className="bg-slate-50">
										<tr>
											<th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Clé</th>
											<th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Dossier</th>
											<th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">
												Taille
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100 bg-white">
										{shown.map((file) => (
											<tr key={file.id ?? file.key}>
												<td className="break-all px-3 py-1.5 font-mono text-xs text-slate-700">{file.key}</td>
												<td className="px-3 py-1.5 text-xs text-slate-500">{file.folder || "-"}</td>
												<td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-700">
													{formatBytes(Number(file.size ?? 0))}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							{hidden > 0 && (
								<p className="text-xs text-slate-500">… et {hidden} autre(s) fichier(s) orphelin(s) non affiché(s).</p>
							)}
						</div>
					)}

					{/* La fuite connue du delete : la ligne de texte extrait survit à son PDF. */}
					{leaks.length > 0 && (
						<div className="space-y-2">
							<p className="text-sm font-semibold text-slate-800">Fuites PdfExtractedTexts ({leaks.length})</p>
							<p className="text-xs text-slate-500">
								Textes extraits de PDF dont le fichier n'existe plus dans le stockage : des lignes SQL laissées par
								d'anciennes suppressions.
							</p>
							<ul className="divide-y divide-amber-100 rounded-xl border border-amber-200 bg-amber-50/50">
								{leaks.map((leak) => (
									<li key={leak.storageKey} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
										<span className="break-all font-mono text-xs text-slate-700">{leak.storageKey}</span>
										<span className="text-[11px] text-slate-500">
											{Number(leak.wordCount ?? 0)} mot(s) · extrait le {formatDateTimeFr(leak.extractedAt)}
										</span>
									</li>
								))}
							</ul>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default MediaOrphansCard;
